import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Search, ArrowRight, Clock, Calendar, BookOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import { formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = process.env.REACT_APP_BACKEND_URL;

const ConteudosPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 9 };
      if (search) params.q = search;
      if (category) params.category = category;
      const { data } = await axios.get(`${API}/content/articles`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { setItems([]); }
    setLoading(false);
  }, [page, search, category]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/content/categories`).then(({ data }) => setCategories(data || [])).catch(() => {});
  }, []);

  const submitSearch = (e) => { e.preventDefault(); setPage(1); setSearch(q); };
  const pickCategory = (c) => { setPage(1); setCategory(c === category ? '' : c); };

  const featured = page === 1 && !search && !category ? items[0] : null;
  const rest = featured ? items.slice(1) : items;

  return (
    <div data-testid="conteudos-page">
      <Helmet>
        <title>Central de Conteúdos | HM Talent — Carreira, RH e Recrutamento</title>
        <meta name="description" content="Artigos, dicas e insights sobre carreira, recrutamento e gestão de pessoas. A Central de Conteúdos da HM Talent ajuda candidatos e empresas a evoluírem." />
        <meta name="keywords" content="carreira, recrutamento, RH, gestão de pessoas, dicas de emprego, entrevista, HM Talent" />
        <link rel="canonical" href={`${SITE}/conteudos`} />
        <meta property="og:title" content="Central de Conteúdos | HM Talent" />
        <meta property="og:description" content="Artigos e dicas sobre carreira, recrutamento e RH." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE}/conteudos`} />
      </Helmet>

      {/* Hero */}
      <section className="hm-gradient text-white py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        <div className="container-hm px-6 text-center max-w-3xl mx-auto relative">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-sm font-semibold bg-white/15 px-4 py-1.5 rounded-full backdrop-blur">
              <BookOpen className="w-4 h-4" /> Central de Conteúdos
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold mt-5">Conteúdo que impulsiona carreiras e empresas</h1>
            <p className="mt-4 text-white/90 text-lg">Dicas práticas, tendências e estratégias sobre carreira, recrutamento e gestão de pessoas.</p>
            <form onSubmit={submitSearch} className="mt-8 flex gap-2 max-w-xl mx-auto">
              <div className="relative flex-1">
                <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="conteudos-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar artigos..." className="pl-11 h-12 bg-white text-slate-900 border-0 rounded-full" />
              </div>
              <Button data-testid="conteudos-search-btn" type="submit" className="h-12 px-6 rounded-full bg-[#4A1B00] hover:bg-[#3a1500] text-white font-semibold">Buscar</Button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Categorias */}
      {categories.length > 0 && (
        <section className="bg-white border-b border-slate-100 sticky top-[68px] z-20">
          <div className="container-hm px-6 py-4 flex flex-wrap gap-2 items-center">
            <button data-testid="cat-all" onClick={() => pickCategory('')}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${!category ? 'bg-[#FF6B00] text-white' : 'bg-slate-100 text-slate-600 hover:bg-orange-50'}`}>Todos</button>
            {categories.map((c) => (
              <button key={c.category} data-testid={`cat-${c.category}`} onClick={() => pickCategory(c.category)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${category === c.category ? 'bg-[#FF6B00] text-white' : 'bg-slate-100 text-slate-600 hover:bg-orange-50'}`}>
                {c.category} <span className="opacity-60">({c.count})</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Lista */}
      <section className="section-padding bg-[#FFF9F4] min-h-[50vh]">
        <div className="container-hm px-6">
          {(search || category) && (
            <div className="mb-6 text-slate-600 text-sm" data-testid="conteudos-filter-info">
              {total} artigo(s) encontrado(s){search && <> para "<strong>{search}</strong>"</>}{category && <> em <strong>{category}</strong></>}
              <button onClick={() => { setQ(''); setSearch(''); setCategory(''); setPage(1); }} className="ml-3 text-[#FF6B00] font-semibold hover:underline">Limpar filtros</button>
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 text-slate-500" data-testid="conteudos-empty">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-semibold">Nenhum artigo publicado ainda.</p>
              <p className="text-sm mt-1">Em breve traremos conteúdos incríveis para você.</p>
            </div>
          ) : (
            <>
              {featured && (
                <Reveal>
                  <Link to={`/conteudos/${featured.slug}`} data-testid={`article-featured-${featured.slug}`}
                    className="grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden bg-white shadow-lg hover-lift mb-10 group">
                    <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-slate-100">
                      {featured.cover_image_url
                        ? <img src={featured.cover_image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        : <div className="w-full h-full hm-gradient flex items-center justify-center"><BookOpen className="w-16 h-16 text-white/40" /></div>}
                    </div>
                    <div className="p-8 lg:p-10 flex flex-col justify-center">
                      {featured.category && <span className="text-xs font-bold text-[#FF6B00] uppercase tracking-wide">{featured.category}</span>}
                      <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-900 mt-2 group-hover:text-[#FF6B00] transition-colors">{featured.title}</h2>
                      <p className="mt-3 text-slate-600 line-clamp-3">{featured.excerpt}</p>
                      <div className="mt-5 flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatDate(featured.published_at)}</span>
                        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {featured.reading_minutes} min</span>
                      </div>
                      <span className="mt-5 inline-flex items-center gap-2 font-semibold text-[#FF6B00]">Ler artigo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </Link>
                </Reveal>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((a, idx) => (
                  <Reveal key={a.id} variant="fadeUp" delay={Math.min(idx * 60, 300)}>
                    <Link to={`/conteudos/${a.slug}`} data-testid={`article-card-${a.slug}`}
                      className="flex flex-col h-full rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl hover-lift group border border-slate-100">
                      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                        {a.cover_image_url
                          ? <img src={a.cover_image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          : <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center"><BookOpen className="w-10 h-10 text-orange-300" /></div>}
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        {a.category && <span className="text-xs font-bold text-[#FF6B00] uppercase tracking-wide">{a.category}</span>}
                        <h3 className="text-lg font-bold text-slate-900 mt-1.5 line-clamp-2 group-hover:text-[#FF6B00] transition-colors">{a.title}</h3>
                        <p className="mt-2 text-sm text-slate-600 line-clamp-3 flex-1">{a.excerpt}</p>
                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDate(a.published_at)}</span>
                          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {a.reading_minutes} min</span>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>

              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-12" data-testid="conteudos-pagination">
                  <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full">Anterior</Button>
                  <span className="flex items-center px-4 text-sm text-slate-600">Página {page} de {pages}</span>
                  <Button variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-full">Próxima</Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ConteudosPage;
